import React, { useEffect } from 'react';
import { Navigation } from './components/Navigation';
import { Footer } from './components/Footer';
import Dashboard from './components/Dashboard';
import { Login } from './components/Auth/Login';
import { Register } from './components/Auth/Register';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Clients } from './pages/dashboard/Clients';
import Products from './pages/dashboard/Products';
import Sales from './pages/dashboard/Sales';
import { Reports } from './pages/dashboard/Reports';
import { Settings } from './pages/dashboard/Settings';
import { AuthGuard } from './components/AuthGuard';
import { useAuth } from './contexts/AuthContext';
import { useLanguage } from './contexts/LanguageContext';
import { Home } from './components/Home';
import { WhatsAppChat } from './components/WhatsAppChat';
import AllSales from './pages/dashboard/AllSales';
import DashboardHome from './pages/dashboard/Home';
import SEO from './components/SEO';
import Annonce from './pages/public/Annonce';
import PrivacyPolicy from './pages/public/PrivacyPolicy';
import Terms from './pages/public/Terms';
import { HelmetProvider } from "react-helmet-async";
import Contact from './pages/public/Contact';
import DeleteAccount from './pages/public/DeleteAccount';
import SchoolDashboard from './components/SchoolDashboard';
import SchoolHome from './pages/school/Home';
import Students from './pages/school/Students';
import Classes from './pages/school/Classes';
import Teachers from './pages/school/Teachers';
import SchoolFees from './pages/school/SchoolFees';
import Payments from './pages/school/Payments';
import SchoolSettings from './pages/school/Settings';
import SchoolDashboardPage from './pages/school/Dashboard';
import Communication from './pages/school/Communication';
import HotelDashboard from './components/HotelDashboard';
import HotelDashboardPage from './pages/hotel/Dashboard';
import HotelRooms from './pages/hotel/Rooms';
import HotelBookings from './pages/hotel/Bookings';
import HotelBookingForm from './pages/hotel/BookingForm';
import HotelReports from './pages/hotel/Reports';
import HotelInvoices from './pages/hotel/Invoices';
import HotelSettings from './pages/hotel/Settings';

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const { language } = useLanguage();
  
  // Détermine si on affiche certains composants en fonction de l'URL
  const currentPath = location.pathname;
  const isDashboardRoute = currentPath.includes('/dashboard') || currentPath.includes('/school-dashboard') || currentPath.includes('/hotel-dashboard');
  const isAnnoncePage = currentPath.includes('/annonce/');
  const isPrivacyPolicyPage = currentPath.includes('/privacy-policy');
  const isTermsPage = currentPath.includes('/terms');
  const isAuthRoute = ['/fr/login', '/en/login', '/fr/register', '/en/register', '/login', '/register'].includes(currentPath);
  
  // Afficher le footer sur toutes les pages publiques (ni dashboard, ni authentification, ni annonce, ni privacy-policy, ni terms)
  const showFooter = !isDashboardRoute && !isAuthRoute && !isAnnoncePage && !isPrivacyPolicyPage && !isTermsPage;
  
  const showNavigation = !isDashboardRoute && !isAnnoncePage && !isAuthRoute && !isPrivacyPolicyPage && !isTermsPage;
  const needsPadding = showNavigation;
  
  // Redirection si l'URL n'a pas de préfixe de langue
  useEffect(() => {
    const path = location.pathname;
    // Si l'URL ne commence pas par /fr ou /en et n'est pas vide
    if (path !== '/' && !path.startsWith('/fr') && !path.startsWith('/en')) {
      navigate(`/${language}${path}`);
    }
    // Si l'URL est juste /, rediriger vers la langue courante
    if (path === '/') {
      navigate(`/${language}`);
    }
  }, [location.pathname, language, navigate]);

  return (
    <HelmetProvider>
      <SEO />
      <div className="min-h-screen">
        {showNavigation && <Navigation />}
        <div className={needsPadding ? 'pt-24' : ''}>
          <Routes>
            {/* Route racine - redirige vers la langue détectée */}
            <Route path="/" element={<Navigate to={`/${language}`} replace />} />
            
            {/* Routes avec préfixe de langue */}
            {['fr', 'en'].map(lang => (
              <Route key={lang} path={`/${lang}`} element={<Home />} />
            ))}
            
            {/* Routes d'authentification par langue */}
            {['fr', 'en'].map(lang => {
              const getAuthRedirect = () => {
                const category = user?.company?.category?.name;
                if (category === 'Hotel') return `/${lang}/hotel-dashboard`;
                if (category === 'École') return `/${lang}/school-dashboard`;
                return `/${lang}/dashboard`;
              };
              return (
              <React.Fragment key={`auth-${lang}`}>
                <Route path={`/${lang}/login`} element={
                  isAuthenticated ? <Navigate to={getAuthRedirect()} replace /> : <Login />
                } />
                <Route path={`/${lang}/register`} element={
                  isAuthenticated ? <Navigate to={getAuthRedirect()} replace /> : <Register />
                } />
              </React.Fragment>
              );
            })}
            
            {/* Routes d'annonces pour chaque langue */}
            {['fr', 'en'].map(lang => (
              <Route key={`annonce-${lang}`} path={`/${lang}/annonce/:id`} element={<Annonce />} />
            ))}
            
            {/* Routes de politique de confidentialité */}
            <Route path="/fr/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/en/privacy-policy" element={<PrivacyPolicy />} />
            
            {/* Routes de conditions d'utilisation */}
            <Route path="/fr/terms" element={<Terms />} />
            <Route path="/en/terms" element={<Terms />} />
            
            {/* Routes de tableau de bord - Anglais */}
            <Route path="/en/dashboard" element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }>
              <Route index element={<DashboardHome />} />
              <Route path="clients" element={<Clients />} />
              <Route path="products" element={<Products />} />
              <Route path="sales" element={<Sales />} />
              <Route path="all-sales" element={<AllSales />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
            </Route>
            
            {/* Routes de tableau de bord - Français */}
            <Route path="/fr/dashboard" element={
              <AuthGuard>
                <Dashboard />
              </AuthGuard>
            }>
              <Route index element={<DashboardHome />} />
              <Route path="clients" element={<Clients />} />
              <Route path="produits" element={<Products />} />
              <Route path="ventes" element={<Sales />} />
              <Route path="toutes-les-ventes" element={<AllSales />} />
              <Route path="rapports" element={<Reports />} />
              <Route path="parametres" element={<Settings />} />
            </Route>

            {/* Routes du module école - Anglais */}
            <Route path="/en/school-dashboard" element={
              <AuthGuard>
                <SchoolDashboard />
              </AuthGuard>
            }>
              <Route index element={<SchoolDashboardPage />} />
              <Route path="home" element={<SchoolHome />} />
              <Route path="students" element={<Students />} />
              <Route path="classes" element={<Classes />} />
              <Route path="teachers" element={<Teachers />} />
              <Route path="school-fees" element={<SchoolFees />} />
              <Route path="payments" element={<Payments />} />
              <Route path="communication" element={<Communication />} />
              <Route path="settings" element={<SchoolSettings />} />
            </Route>

            {/* Routes du module école - Français */}
            <Route path="/fr/school-dashboard" element={
              <AuthGuard>
                <SchoolDashboard />
              </AuthGuard>
            }>
              <Route index element={<SchoolDashboardPage />} />
              <Route path="accueil" element={<SchoolHome />} />
              <Route path="eleves" element={<Students />} />
              <Route path="classes" element={<Classes />} />
              <Route path="enseignants" element={<Teachers />} />
              <Route path="frais-scolaires" element={<SchoolFees />} />
              <Route path="paiements" element={<Payments />} />
              <Route path="communiquer" element={<Communication />} />
              <Route path="parametres" element={<SchoolSettings />} />
            </Route>
            
            {/* Routes du module hôtel - Anglais */}
            <Route path="/en/hotel-dashboard" element={
              <AuthGuard>
                <HotelDashboard />
              </AuthGuard>
            }>
              <Route index element={<HotelDashboardPage />} />
              <Route path="rooms" element={<HotelRooms />} />
              <Route path="bookings" element={<HotelBookings />} />
              <Route path="new-booking" element={<HotelBookingForm />} />
              <Route path="reports" element={<HotelReports />} />
              <Route path="invoices" element={<HotelInvoices />} />
              <Route path="settings" element={<HotelSettings />} />
            </Route>

            {/* Routes du module hôtel - Français */}
            <Route path="/fr/hotel-dashboard" element={
              <AuthGuard>
                <HotelDashboard />
              </AuthGuard>
            }>
              <Route index element={<HotelDashboardPage />} />
              <Route path="chambres" element={<HotelRooms />} />
              <Route path="reservations" element={<HotelBookings />} />
              <Route path="nouvelle-reservation" element={<HotelBookingForm />} />
              <Route path="rapports" element={<HotelReports />} />
              <Route path="factures" element={<HotelInvoices />} />
              <Route path="parametres" element={<HotelSettings />} />
            </Route>

            {/* Pour la compatibilité avec les anciennes URLs */}
            <Route path="/login" element={<Navigate to={`/${language}/login`} replace />} />
            <Route path="/register" element={<Navigate to={`/${language}/register`} replace />} />
            <Route path="/dashboard/*" element={<Navigate to={`/${language}/dashboard`} replace />} />
            <Route path="/annonce/:id" element={<Navigate to={`/${language}/annonce/:id`} replace />} />

            {/* Route de contact */}
            <Route path="/fr/contact" element={<Contact />} />
            <Route path="/en/contact" element={<Contact />} />

            {/* Route de suppression de compte */}
            <Route path="/fr/delete-account" element={<DeleteAccount />} />
            <Route path="/en/delete-account" element={<DeleteAccount />} />

            {/* Route par défaut - redirige vers la home dans la langue courante */}
            <Route path="*" element={<Navigate to={`/${language}`} replace />} />
          </Routes>
        </div>
        {!isAnnoncePage && <WhatsAppChat />}
      </div>
      {showFooter && <Footer />}
    </HelmetProvider>
  );
}

export default App;